// The Briefing card a mail FOLDER gets (busy-my-web#319, the 2026-09-01 batch).
//
// Every folder page in the batch — Important, Draft, Trash, Spam, Scheduled — draws the same card
// at the foot of the page as the inbox does, but about the folder it is on rather than the whole
// mailbox: "Briefing Important Emails" over one count row ("9 Important emails"), or, with the
// folder empty, over one sentence ("No marked as important messages.").
//
// Two of them carry a maintenance line as well — Trash and Spam both say what the 30-day sweep
// does, and both offer the button that does it now (frames 40010510-237217, 40010530-247533) —
// with a confirm step (40010525-245287) and a receipt when it is done (40010526-246078).
//
// Plain data and one builder, so what a folder's card SAYS can be checked without the strip.

import { briefing, countLabel } from './rows'
import {
  EMAIL_TITLE_COLOR,
  EMAIL_TITLE_ICON,
  emailBriefingGridOrder,
  emailDashboardRows
} from './email'
import { TEXT } from '@/lib/emailSession'

/** The kind the strip resolves this card by — not `emails`, which is the inbox SESSION's card. */
export const FOLDER_KIND = 'email-folder'

// busy-my-web#609 (Saffet 2026-10-09): the shared `countLabel` — thousands separator, capital after the count.
const plural = (n, word) => countLabel(n, word)

/**
 * Per folder: the card's title, the glyph and wording of its one count row, the line it shows
 * with nothing in it, and — for the two folders that are swept — the maintenance line, the button
 * that runs the sweep now, what the confirm asks and what the receipt says afterwards.
 *
 * The wording is the frames'. Their sample copy calls Trash's row "2 draft emails", which is the
 * draft card's line left in place; the row here follows the pattern the other four frames set
 * ("<count> <folder> emails") so a mailbox never states a count under the wrong noun.
 */
export const FOLDER_BRIEFINGS = {
  important: {
    title: 'Briefing Important Emails',
    icon: 'direct-inbox',
    label: (n) => plural(n, 'Important email'),
    empty: 'No marked as important messages.'
  },
  draft: {
    title: 'Briefing draft Emails',
    icon: 'draft',
    label: (n) => plural(n, 'draft email'),
    empty: 'No saved draft messages.'
  },
  bin: {
    title: 'Briefing trash Emails',
    icon: 'bin',
    label: (n) => plural(n, 'trash email'),
    empty: 'No conversations in Trash.',
    note: 'Messages that have been in Trash more than 30 days will be automatically deleted.',
    action: 'Empty Trash now',
    confirm: 'Delete all conversation forever?',
    done: 'All message have been deleted'
  },
  spam: {
    title: 'Briefing spam Emails',
    icon: 'spam',
    label: (n) => plural(n, 'spam email'),
    empty: 'No conversations in Spam.',
    note: 'Messages that have been in Spam more than 30 days will be automatically deleted.',
    action: 'Delete all spam message',
    confirm: 'Delete all conversation forever?',
    done: 'All message have been deleted'
  },
  scheduled: {
    title: 'Briefing scheduled Emails',
    icon: 'past',
    // The frame writes "3 schedule emails"; kept, because the card is the design's voice.
    label: (n) => plural(n, 'schedule email'),
    empty: 'No scheduled email.'
  }
}

/**
 * The INBOX's card is the mailbox dashboard itself (frames 40009919-303063, 40009931-304382 and
 * the four action-notice frames all draw the listing page with it at the foot): "Briefing Email
 * Inbox" over the two-column grid of counts.
 *
 * It is built here, from the counts alone, rather than by asking for the sender GROUPS the way the
 * workspace card does — declaring groups is what hands the middle column to the Email Workspace
 * (lib/emailSession.js's `emailWorkspaceOwnsMiddle`), and the listing page must keep its listing.
 * The rows are plain facts here for the same reason: narrowing the card's category is the
 * workspace's gesture, and there is no workspace under this one.
 */
export const INBOX_FOLDER = 'inbox'

export function emailInboxCard(counts) {
  // Plain facts, one column each: the entries PAIR UP in the frame's grid (a full-width row is
  // this card's summary sentence, and this card has none).
  const rows = emailDashboardRows(counts).map((r) => ({ key: r.key, icon: r.icon, text: r.text, span: 1 }))
  return {
    ...briefing({
      kind: FOLDER_KIND,
      title: TEXT.briefingInbox,
      titleIcon: EMAIL_TITLE_ICON,
      titleColor: EMAIL_TITLE_COLOR,
      columns: rows.length > 1 ? 2 : 1,
      rows: rows.length > 1 ? emailBriefingGridOrder(rows) : rows
    }),
    folder: INBOX_FOLDER,
    count: rows.length,
    // Nothing known yet is stated the way every other folder states nothing.
    empty: rows.length ? '' : 'Nothing in your inbox to brief right now.',
    note: '',
    action: '',
    confirm: '',
    done: ''
  }
}

/** The folders that have a card of their own. Anything else keeps whatever the strip was showing. */
export const FOLDER_IDS = [INBOX_FOLDER, ...Object.keys(FOLDER_BRIEFINGS)]

export const hasFolderBriefing = (folder) => FOLDER_IDS.includes(String(folder || ''))

/** The confirm's two buttons, named once so the panel and its tests read the same words. */
export const CONFIRM_LABELS = { cancel: 'Cancel', confirm: 'Delete' }

/**
 * The card for one folder.
 *
 * @param folder the page's sub-menu id (inbox, important, draft, bin, spam, scheduled)
 * @param count how many the folder holds right now
 * @param counts the mailbox dashboard's counts — the inbox's card is made of those rather than of
 *   one number (see `emailInboxCard`); ignored by every other folder.
 * @returns the briefing value, with the folder's own extras riding on it — `empty` (the line to
 *   draw instead of a row), `note`/`action` (the sweep, only where there is something to sweep)
 *   and `confirm`/`done` (what the two later steps say). Null for a folder with no card.
 */
export function emailFolderBriefing(folder, { count = 0, counts = null } = {}) {
  if (String(folder || '') === INBOX_FOLDER) return emailInboxCard(counts)
  const spec = FOLDER_BRIEFINGS[String(folder || '')]
  if (!spec) return null
  const n = Math.max(0, Number(count) || 0)
  const built = briefing({
    kind: FOLDER_KIND,
    title: spec.title,
    // The same header the inbox card wears (busy-my-web#316): the receipt glyph, near-black.
    titleIcon: EMAIL_TITLE_ICON,
    titleColor: EMAIL_TITLE_COLOR,
    rows: n > 0 ? [{ key: `${folder}-count`, icon: spec.icon, text: spec.label(n) }] : []
  })
  return {
    ...built,
    folder: String(folder),
    count: n,
    // An empty folder states its emptiness in words; a full one states its count in a row.
    empty: n > 0 ? '' : spec.empty,
    // …and the sweep is offered only while there is something to sweep. An empty Trash showing
    // "Empty Trash now" would be a button that does nothing (frame 40010510-236283 has neither).
    note: n > 0 ? spec.note || '' : '',
    action: n > 0 ? spec.action || '' : '',
    confirm: spec.confirm || '',
    done: spec.done || ''
  }
}

export default emailFolderBriefing
